import { useState, useEffect, useMemo } from 'react'
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { createAppTheme } from '~/styles/mui-theme'
import { ThemeContext } from './theme-context'

const THEME_STORAGE_KEY = 'terra_theme_mode'

// Get OS preference for color scheme
const getSystemPreference = () => {
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export const ThemeContextProvider = ({ children }) => {
  // Load theme mode from localStorage or default to 'system'
  const [mode, setMode] = useState(() => {
    const stored = localStorage.getItem(THEME_STORAGE_KEY)
    return (stored === 'dark' || stored === 'light' || stored === 'system') ? stored : 'system'
  })

  // Track OS preference
  const [systemPreference, setSystemPreference] = useState(() => getSystemPreference())

  // Calculate resolved mode (actual theme to use)
  const resolvedMode = useMemo(() => {
    return mode === 'system' ? systemPreference : mode
  }, [mode, systemPreference])

  // Listen for OS preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    
    const handleChange = (e) => {
      setSystemPreference(e.matches ? 'dark' : 'light')
    }

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    } 
    // Fallback for older browsers
    else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange)
      return () => mediaQuery.removeListener(handleChange)
    }
  }, [])

  // Persist theme mode to localStorage
  useEffect(() => {
    localStorage.setItem(THEME_STORAGE_KEY, mode)
  }, [mode])

  const theme = useMemo(() => createAppTheme(resolvedMode), [resolvedMode])

  const value = {
    mode,
    resolvedMode,
    setMode,
  }

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  )
}
